import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import Nav from "../Components/Nav/Nav";
import Login from "./Login";
import AdminMenu from "../Components/Admin/AdminMenu";
import AdminFormHeader from "../Components/Admin/AdminFormHeader";
import { ToastContainer, toast } from "react-toastify";
import { ArrowLeft, Eye, RotateCw, MousePointerClick, AlertTriangle } from "lucide-react";
import CurriculumTree from "../Components/FormationBuilder/CurriculumTree";
import LessonEditor from "../Components/FormationBuilder/LessonEditor";
import QuizEditor from "../Components/FormationBuilder/QuizEditor";
import {
  getFormation,
  createModule,
  updateModule,
  deleteModule,
  publishModuleVersion,
  createLesson,
  updateLesson,
  deleteLesson,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  regeneratePart,
  publishFormation,
  unpublishFormation,
  publishFinalVersion,
  updateFormation,
} from "../actions/formations.actions";
import { getFormationCategories } from "../actions/formationCategory.actions";

const sortByOrder = (arr) => [...(arr || [])].sort((a, b) => a.order - b.order);

const AdminFormationBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userData = useSelector((s) => s.userReducer);
  const { current } = useSelector((s) => s.formationsReducer);
  const categories = useSelector((s) => s.formationCategoryReducer);
  const [selected, setSelected] = useState({ type: null, id: null });
  // Modifs non enregistrées dans l'éditeur de leçon (remonté par LessonEditor)
  const [editorDirty, setEditorDirty] = useState(false);

  const isAllowed = userData.isAdmin || userData.isReferent;

  useEffect(() => {
    dispatch(getFormation(id));
    dispatch(getFormationCategories());
  }, [id, dispatch]);

  // Message transmis par la page de génération via l'état de navigation
  // (les toasts émis avant navigate() sont détruits — ToastContainer local).
  useEffect(() => {
    const s = location.state;
    if (s && (s.warning || s.success)) {
      if (s.warning) toast.warn(s.warning);
      if (s.success) toast.success(s.success);
      // Nettoie l'état pour ne pas re-toaster au refresh
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  // Génération asynchrone : /generate répond dès le plan créé, puis les leçons et
  // quizz se remplissent côté serveur. Tant que le statut est "generating", on
  // recharge la formation régulièrement pour voir le contenu apparaître.
  const generationStatus =
    current && String(current.id) === String(id) ? current.generationStatus : null;
  useEffect(() => {
    if (generationStatus !== "generating") return;
    const timer = setInterval(() => {
      dispatch(getFormation(id)).catch(() => {});
    }, 4000);
    return () => clearInterval(timer);
  }, [generationStatus, id, dispatch]);

  // Toast de fin de génération (une seule fois, à la transition de statut).
  const prevGenerationStatus = useRef(null);
  useEffect(() => {
    if (prevGenerationStatus.current === "generating") {
      if (generationStatus === "completed") {
        toast.success("Génération terminée : leçons et quizz sont prêts");
      }
      if (generationStatus === "failed") {
        toast.error("La génération s'est interrompue : régénérez les morceaux manquants");
      }
    }
    prevGenerationStatus.current = generationStatus;
  }, [generationStatus]);

  if (!userData || !userData.id || !isAllowed) return <Login />;

  if (!current || String(current.id) !== String(id)) {
    return (
      <>
        <Nav />
        <div className="mainContainer">
          <AdminMenu />
          <div className="centerBlock_admin">
            <p>Chargement…</p>
          </div>
        </div>
      </>
    );
  }

  const refresh = () => dispatch(getFormation(id));

  // Retourne true en succès, false en échec : les actions chaînées qui
  // supposent le succès (ex. maybeNotifyRetake) doivent tester ce booléen.
  const wrap = async (promise, okMsg) => {
    try {
      await promise;
      if (okMsg) toast.success(okMsg);
      await refresh();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || "Action impossible");
      return false;
    }
  };

  // Confirmation avant toute action qui remonterait l'éditeur de leçon
  // (perte des modifications non enregistrées).
  const confirmDiscardIfDirty = () =>
    !editorDirty ||
    window.confirm("Des modifications de la leçon ne sont pas enregistrées. Continuer et les perdre ?");

  const modules = sortByOrder(current.FormationModules);

  const actions = {
    addModule: () => wrap(dispatch(createModule(current.id, "Nouveau module"))),
    renameModule: (mid, title) => wrap(dispatch(updateModule(mid, { title }))),
    deleteModule: (m) => {
      if (window.confirm(`Supprimer le module "${m.title}" ?`)) wrap(dispatch(deleteModule(m.id)), "Module supprimé");
    },
    publishModuleVersion: (m) => {
      if (window.confirm("Publier une nouvelle version de ce module ? Les apprenants devront le repasser et les certificats concernés seront suspendus."))
        wrap(dispatch(publishModuleVersion(m.id)), "Nouvelle version publiée");
    },
    addLesson: (mid) => wrap(dispatch(createLesson(mid, { title: "Nouvelle leçon" }))),
    deleteLesson: (l) => {
      if (window.confirm(`Supprimer la leçon "${l.title}" ?`)) {
        wrap(dispatch(deleteLesson(l.id)), "Leçon supprimée");
      }
    },
    // Réordonne localement puis renumérote (order = index) : l'échange de
    // deux valeurs order est un no-op quand elles sont dupliquées (possible
    // après suppression, le backend ne renumérote pas).
    moveModule: (m, dir) => {
      const idx = modules.findIndex((x) => x.id === m.id);
      const j = idx + dir;
      if (j < 0 || j >= modules.length) return;
      const next = [...modules];
      [next[idx], next[j]] = [next[j], next[idx]];
      const changed = next
        .map((mod, i) => ({ mod, i }))
        .filter(({ mod, i }) => mod.order !== i);
      if (!changed.length) return;
      wrap(Promise.all(changed.map(({ mod, i }) => dispatch(updateModule(mod.id, { order: i })))));
    },
    moveLesson: (m, l, dir) => {
      const ls = sortByOrder(m.FormationLessons);
      const idx = ls.findIndex((x) => x.id === l.id);
      const j = idx + dir;
      if (j < 0 || j >= ls.length) return;
      const next = [...ls];
      [next[idx], next[j]] = [next[j], next[idx]];
      const changed = next
        .map((les, i) => ({ les, i }))
        .filter(({ les, i }) => les.order !== i);
      if (!changed.length) return;
      // Le PUT sur la leçon sélectionnée change sa key (updatedAt) et
      // remonte l'éditeur : demander confirmation si modifs non sauvées.
      const touchesSelected =
        selected.type === "lesson" && changed.some(({ les }) => les.id === selected.id);
      if (touchesSelected && !confirmDiscardIfDirty()) return;
      if (touchesSelected) setEditorDirty(false);
      wrap(Promise.all(changed.map(({ les, i }) => dispatch(updateLesson(les.id, { order: i })))));
    },
  };

  let selectedLesson = null;
  let selectedLessonModuleId = null;
  let selectedQuiz = null;
  if (selected.type === "lesson") {
    for (const m of modules) {
      const f = (m.FormationLessons || []).find((x) => x.id === selected.id);
      if (f) {
        selectedLesson = f;
        selectedLessonModuleId = m.id;
        break;
      }
    }
  } else if (selected.type === "quiz") {
    selectedQuiz = (current.FormationQuizzes || []).find((x) => x.id === selected.id) || null;
  }

  // Après une modif de contenu sur une formation PUBLIÉE, proposer de notifier
  // les apprenants ayant déjà validé (publie une nouvelle version du module).
  const maybeNotifyRetake = (moduleId) => {
    if (!moduleId || current.status !== "published") return;
    const ok = window.confirm(
      "Cette formation est publiée. Notifier les apprenants ayant déjà validé de repasser ce module ?\n\n" +
        "Une nouvelle version du module sera publiée et les certificats concernés seront suspendus."
    );
    if (ok) wrap(dispatch(publishModuleVersion(moduleId)), "Apprenants notifiés — nouvelle version publiée");
  };

  const changeCategory = (value) =>
    wrap(dispatch(updateFormation(current.id, { categoryId: value === "" ? null : Number(value) })), "Catégorie mise à jour");

  const togglePublish = () =>
    current.status === "published"
      ? wrap(dispatch(unpublishFormation(current.id)), "Formation dépubliée")
      : wrap(dispatch(publishFormation(current.id)), "Formation publiée");

  return (
    <>
      <ToastContainer />
      <Nav />
      <div className="mainContainer">
        <AdminMenu />
        <div className="centerBlock_admin">
          <AdminFormHeader
            title={current.title}
            subtitle="Édition du module — leçons, quizz et publication."
            back={
              <button className="mini-btn" onClick={() => navigate("/admin/formations")}>
                <ArrowLeft size={14} /> Retour
              </button>
            }
            actions={
              <>
                <label htmlFor="builder-category" className="builder-category-label">
                  Catégorie
                </label>
                <select
                  id="builder-category"
                  className="builder-category-select"
                  value={current.categoryId ?? ""}
                  onChange={(e) => changeCategory(e.target.value)}
                >
                  <option value="">— Aucune —</option>
                  {Array.isArray(categories) &&
                    categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </select>
                <span className={current.status === "published" ? "success-span" : "error-span"}>
                  {current.status === "published" ? "Publiée" : "Brouillon"}
                </span>
                <Link to={`/formations/${current.id}`}>
                  <button className="mini-btn"><Eye size={14} /> Prévisualiser</button>
                </Link>
                <button
                  className="ai-btn"
                  onClick={() => {
                    if (window.confirm("Publier une nouvelle version du quizz final ? Les apprenants devront repasser le quizz final."))
                      wrap(dispatch(publishFinalVersion(current.id)), "Version finale publiée");
                  }}
                >
                  <RotateCw size={14} /> version finale
                </button>
                <button
                  className="greenButton"
                  onClick={togglePublish}
                  disabled={current.generationStatus === "generating"}
                >
                  {current.status === "published" ? "Dépublier" : "Publier"}
                </button>
              </>
            }
          />

          {current.generationStatus === "generating" && (
            <p className="textePoucents">
              <RotateCw size={16} /> Génération en cours : l'IA rédige les leçons et les quizz,
              la page se met à jour automatiquement…
            </p>
          )}
          {current.generationStatus === "failed" && (
            <p className="textePoucents">
              <AlertTriangle size={16} /> La génération s'est interrompue : le contenu est
              incomplet. Régénérez les leçons ou quizz manquants depuis le curriculum.
            </p>
          )}

          <div className="builder-layout">
            <CurriculumTree
              formation={current}
              selected={selected}
              onSelect={(type, sid) => {
                if (selected.type === type && selected.id === sid) return;
                if (!confirmDiscardIfDirty()) return;
                setEditorDirty(false);
                setSelected({ type, id: sid });
              }}
              actions={actions}
            />

            <div className="builder-editor">
              {selectedLesson && (
                <LessonEditor
                  key={`${selectedLesson.id}:${selectedLesson.updatedAt}`}
                  lesson={selectedLesson}
                  onDirtyChange={setEditorDirty}
                  onSave={(fields) =>
                    wrap(dispatch(updateLesson(selectedLesson.id, fields)), "Leçon enregistrée").then((ok) => {
                      // Ne proposer la republication du module que si la
                      // sauvegarde a réellement réussi.
                      if (ok) maybeNotifyRetake(selectedLessonModuleId);
                      return ok;
                    })
                  }
                  onRegenerateLesson={() => wrap(dispatch(regeneratePart({ target: "lesson", lessonId: selectedLesson.id })), "Leçon régénérée")}
                  onRegenerateBlock={(blockId) => wrap(dispatch(regeneratePart({ target: "block", lessonId: selectedLesson.id, blockId })), "Bloc régénéré")}
                />
              )}
              {selectedQuiz && (
                <QuizEditor
                  key={selectedQuiz.id}
                  quiz={selectedQuiz}
                  onAddQuestion={() => wrap(dispatch(createQuestion(selectedQuiz.id, { type: "single", text: "Nouvelle question", options: [] })))}
                  onSaveQuestion={(qid, fields) => wrap(dispatch(updateQuestion(qid, fields)), "Question enregistrée")}
                  onDeleteQuestion={(qid) => wrap(dispatch(deleteQuestion(qid)), "Question supprimée")}
                  onRegenerateQuestion={(qid) => wrap(dispatch(regeneratePart({ target: "question", questionId: qid })), "Question régénérée")}
                  onRegenerateQuiz={() => wrap(dispatch(regeneratePart({ target: "quiz", quizId: selectedQuiz.id })), "Quizz régénéré")}
                />
              )}
              {!selectedLesson && !selectedQuiz && (
                <div className="builder-editor-placeholder">
                  <MousePointerClick size={40} />
                  <p className="placeholder-title">Sélectionnez un élément à éditer</p>
                  <p className="placeholder-sub">
                    Choisissez une leçon ou un quizz dans le curriculum à gauche pour en modifier le
                    contenu, ou régénérez-le avec l'IA.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminFormationBuilder;
